import { Box, Stack, Typography } from '@mui/material';
import { ComponentType } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { BoxedIcon } from '@/components/display/Ribbon/BoxedIcon';
import { AlertTriangleFilledIcon } from '@/components/icons/AlertTriangleFilledIcon';
import { CheckCircleIcon, IconProps } from '@/components/icons/CheckCircleIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { PopperContent } from '@/components/poppers/PopperContent';
import { RotatingArrows } from '@/components/progress/RotatingArrows';
import { useCurrentTime } from '@/hooks/useCurrentTime';
import { useIsElementHovered } from '@/hooks/useIsElementHovered';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';

import { formatTimestamp } from './ActivityItem.utils';

export type ActivityStatus = 'inProgress' | 'completed' | 'failed';

export interface ActivityItemProps {
  Icon: ComponentType<IconProps>;
  householdName?: string;
  /** When present, the household name links to this path. */
  householdPath?: string;
  onHouseholdClick?: () => void;
  title: string;
  timestamp: Date;
  failureMessage?: string;
  status: ActivityStatus;
}

function getIconBackgroundColor(status: ActivityStatus): string {
  if (status === 'failed') return COLORS.FUNCTIONAL.ERROR[50];
  if (status === 'inProgress') return COLORS.NAVY[100];
  return COLORS.GRAY[100];
}

function getIconColor(status: ActivityStatus): string {
  if (status === 'failed') return COLORS.FUNCTIONAL.ERROR.DEFAULT;
  if (status === 'inProgress') return COLORS.NAVY[400];
  return COLORS.GRAY[300];
}

function getTitleColor(status: ActivityStatus): string | undefined {
  return status === 'failed' ? COLORS.FUNCTIONAL.ERROR.DEFAULT : undefined;
}

interface HouseholdLabelProps {
  householdName: string;
  householdPath?: string;
  onHouseholdClick?: () => void;
}

function HouseholdLabel({
  householdName,
  householdPath,
  onHouseholdClick,
}: HouseholdLabelProps) {
  const [isHovered, hoverProps] = useIsElementHovered();

  if (!householdPath) {
    return <Typography variant="eyebrow-xs">{householdName}</Typography>;
  }

  return (
    <Typography
      variant="eyebrow-xs"
      component={RouterLink}
      to={householdPath}
      onClick={onHouseholdClick}
      {...hoverProps}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        textDecoration: 'none',
      }}
    >
      {/* Underline only the name, not the caret. */}
      <Box
        component="span"
        sx={{ textDecoration: isHovered ? 'underline' : 'none' }}
      >
        {householdName}
      </Box>
      {/* Decorative: the household name is the accessible link text. */}
      <ChevronRightIcon size={12} aria-hidden />
    </Typography>
  );
}

export function ActivityItem({
  householdName,
  householdPath,
  onHouseholdClick,
  title,
  timestamp,
  failureMessage,
  Icon,
  status,
}: ActivityItemProps) {
  const currentTime = useCurrentTime();
  const timestampDescriptor = formatTimestamp(timestamp, status, currentTime);
  const isInProgress = status === 'inProgress';
  const isFailed = status === 'failed';

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        p: 2,
      }}
    >
      {/* Icon */}
      <BoxedIcon
        icon={Icon}
        sx={{
          backgroundColor: getIconBackgroundColor(status),
          width: 40,
          height: 40,
          flexShrink: 0,
          outline: `3px solid white`,
          zIndex: 1,
          position: 'relative',
        }}
        iconProps={{
          size: 22,
          color: getIconColor(status),
        }}
      />

      {/* Content */}
      <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box>
          {householdName && (
            <HouseholdLabel
              householdName={householdName}
              householdPath={householdPath}
              onHouseholdClick={onHouseholdClick}
            />
          )}
          <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              component="div"
              variant="title-xs"
              fontWeight={
                isInProgress || isFailed
                  ? FONT_WEIGHTS.bold
                  : FONT_WEIGHTS.regular
              }
              mb={0} // override default header margin
              sx={{
                color: getTitleColor(status),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {title}
            </Typography>
            {failureMessage && (
              <ContextualHelpTooltip>
                <PopperContent body={failureMessage} />
              </ContextualHelpTooltip>
            )}
          </Stack>
        </Box>
        <Typography variant="body-xs">{timestampDescriptor}</Typography>
      </Stack>

      {/* Status Icon */}
      <Box sx={{ flexShrink: 0 }}>
        {status === 'inProgress' ? (
          <RotatingArrows />
        ) : status === 'completed' ? (
          <CheckCircleIcon size={16} color={COLORS.FUNCTIONAL.SUCCESS[600]} />
        ) : (
          <AlertTriangleFilledIcon
            size={16}
            color={COLORS.FUNCTIONAL.ERROR.DEFAULT}
          />
        )}
      </Box>
    </Stack>
  );
}
